import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '../base_svg-icon/component';
import { useEventListener } from '@utils/hooks';

import './styles.scss';

const PDFViewer = ({ url, closePreview }) => {
  const [lib, setLib] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  /*
   * Inititialise pdf.js (https://github.com/mozilla/pdf.js/blob/master/examples/webpack/main.js)
   * Dynamically import pdfjsLib, because it is HUGE
   */
  const init = async () => {
    let pdfjsLib = lib;
    if (!lib) {
      pdfjsLib = await import('pdfjs-dist');
      /*
       * Setting worker path to worker bundle wth a worker from a CDN, so it also works on non-SPA sites (CORS issue).
       * When all sites are running the SPA, we can dynamically import the worker ourselves and replace the CDN.
       * -> const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.js');
       */
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.mjs`;
      pdfjsLib.GlobalEvalSuppression = true;

      setLib(pdfjsLib);
    }

    const loadingTask = (lib || pdfjsLib).getDocument(url);
    loadingTask.promise
      .then((pdfDocument) => {
        setTotalPages(pdfDocument.numPages || 1);
        return pdfDocument.getPage(currentPage).then((pdfPage) => {
          const viewport = pdfPage.getViewport({ scale: 1 });
          const canvas = document.getElementById('pdf-viewer');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          const renderTask = pdfPage.render({
            canvasContext: ctx,
            viewport,
          });
          setPageLoaded(true);
          return renderTask.promise;
        });
      })
      .catch(closePreview);
  };

  useEffect(() => {
    init();
  }, [currentPage]);

  const prevDisabled = (currentPage === 1);
  const nextDisabled = (currentPage === totalPages);

  const nextPage = () => !nextDisabled && setCurrentPage(currentPage + 1);
  const prevPage = () => !prevDisabled && setCurrentPage(currentPage - 1);

  const navigate = (e) => {
    if (e.key === 'ArrowRight') {
      nextPage();
    } else if (e.key === 'ArrowLeft') {
      prevPage();
    }
  };
  useEventListener('keydown', navigate);

  return (

    <div className="pdfbook" data-test-id="pdf-book">

      <div className="pdfbook-top">
        <div className="pdfbook-top--close" onClick={closePreview}>
          <Icon size={100} name="ico-error" />
        </div>
      </div>

      <div className="pdfbook-body">
        <div className={`pdfbook-navigation prev ${prevDisabled ? 'disabled' : ''}`} onClick={prevPage}>
          <Icon size={300} name="ico-arrow-circle-left" />
        </div>

        <div className="pdfbook-content">
          <div className={`pdfbook-content-book ${pageLoaded ? 'visible' : ''}`}>
            <section className="pdfbook-content-page">
              <canvas id="pdf-viewer"/>
            </section>
          </div>
        </div>

        <div className={`pdfbook-navigation next ${nextDisabled ? 'disabled' : ''}`} onClick={nextPage}>
          <Icon size={300} name="ico-arrow-circle-right" />
        </div>
      </div>

      <div className="pdfbook-footer">
        <div className="pdfbook-footer-controls">
          <div className="pdfbook-footer-controls-item">{currentPage} / {totalPages || '?'}</div>
        </div>
      </div>

    </div>
  );
};

PDFViewer.propTypes = {
  url: PropTypes.string,
  closePreview: PropTypes.func,
};

export default PDFViewer;
